import React from 'react';
import { motion } from 'framer-motion';
import { FactoryIcon, TrendingUpIcon, ShieldAlertIcon, AwardIcon, GraduationCapIcon, ShieldCheckIcon } from 'lucide-react';
import { ThreeBackground } from '../components/ThreeBackground';
import { Card3D } from '../components/Card3D';
import { PageTransition } from '../components/PageTransition';
export function ProjectsPage() {
  return <PageTransition data-id="element-341">
      {/* Page Header */}
      <section className="relative bg-navy-900 pt-32 pb-20 overflow-hidden bg-glow" data-id="element-342">
        <ThreeBackground data-id="element-343" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12" data-id="element-344">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-345">
            <span className="text-gold font-bold tracking-widest text-sm uppercase mb-2 block" data-id="element-346">
              Impact
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white" data-id="element-347">
              Impact &amp; Achievements
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Accomplishments Grid */}
      <section className="bg-light py-20" data-id="element-348">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-349">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-350">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-351">
              <Card3D data-id="element-352">
                <div className="bg-white p-8 border-l-4 border-gold shadow-sm h-full" data-id="element-353">
                  <div className="flex items-start justify-between mb-6" data-id="element-354">
                    <div className="p-3 bg-light-alt rounded-lg text-navy-800" data-id="element-355">
                      <FactoryIcon size={28} data-id="element-356" />
                    </div>
                    <span className="text-sm font-bold text-gold bg-gold/10 px-3 py-1 rounded-full" data-id="element-357">
                      150 &amp; 200 MMSCFD
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-ink mb-3" data-id="element-358">
                    Cryogenic Plant Commissioning
                  </h3>
                  <p className="text-ink-muted leading-relaxed" data-id="element-359">
                    Successfully commissioned multiple high-capacity cryogenic
                    gas plants, ensuring safe start-up and operational readiness
                    for facilities processing up to 200 MMSCFD.
                  </p>
                </div>
              </Card3D>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-360">
              <Card3D data-id="element-361">
                <div className="bg-white p-8 border-l-4 border-gold shadow-sm h-full" data-id="element-362">
                  <div className="flex items-start justify-between mb-6" data-id="element-363">
                    <div className="p-3 bg-light-alt rounded-lg text-navy-800" data-id="element-364">
                      <TrendingUpIcon size={28} data-id="element-365" />
                    </div>
                    <span className="text-sm font-bold text-gold bg-gold/10 px-3 py-1 rounded-full" data-id="element-366">
                      40k → 60k BPD
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-ink mb-3" data-id="element-367">
                    Fractionation Plant Expansion
                  </h3>
                  <p className="text-ink-muted leading-relaxed" data-id="element-368">
                    Led the commissioning and operational upgrade of a major
                    fractionation facility, significantly increasing daily
                    throughput from 40,000 to 60,000 barrels per day.
                  </p>
                </div>
              </Card3D>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-369">
              <Card3D data-id="element-370">
                <div className="bg-white p-8 border-l-4 border-gold shadow-sm h-full" data-id="element-371">
                  <div className="flex items-start justify-between mb-6" data-id="element-372">
                    <div className="p-3 bg-light-alt rounded-lg text-navy-800" data-id="element-373">
                      <ShieldAlertIcon size={28} data-id="element-374" />
                    </div>
                    <span className="text-sm font-bold text-gold bg-gold/10 px-3 py-1 rounded-full" data-id="element-375">
                      Zero Production Loss
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-ink mb-3" data-id="element-376">
                    Crisis Response Leadership
                  </h3>
                  <p className="text-ink-muted leading-relaxed" data-id="element-377">
                    Prevented a major production shutdown during a full system
                    failure at Formosa Plastics through rapid troubleshooting
                    and decisive action under pressure.
                  </p>
                </div>
              </Card3D>
            </motion.div>

            {/* Card 4 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-378">
              <Card3D data-id="element-379">
                <div className="bg-white p-8 border-l-4 border-gold shadow-sm h-full" data-id="element-380">
                  <div className="flex items-start justify-between mb-6" data-id="element-381">
                    <div className="p-3 bg-light-alt rounded-lg text-navy-800" data-id="element-382">
                      <AwardIcon size={28} data-id="element-383" />
                    </div>
                    <span className="text-sm font-bold text-gold bg-gold/10 px-3 py-1 rounded-full" data-id="element-384">
                      Director Promotion
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-ink mb-3" data-id="element-385">
                    Operational Excellence Recognition
                  </h3>
                  <p className="text-ink-muted leading-relaxed" data-id="element-386">
                    Promoted to Director-level leadership role as a direct
                    result of sustained operational performance, safety records,
                    and team development.
                  </p>
                </div>
              </Card3D>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Education & Certifications */}
      <section className="bg-white py-20" data-id="element-387">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-388">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-16" data-id="element-389">
            <span className="text-gold font-bold tracking-widest text-sm uppercase mb-2 block" data-id="element-390">
              Credentials
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-ink" data-id="element-391">
              Education &amp; Certifications
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-392">
            {/* Education */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-393">
              <Card3D data-id="element-394">
                <div className="bg-light-alt p-8 border border-gray-100" data-id="element-395">
                  <div className="flex items-center mb-6" data-id="element-396">
                    <GraduationCapIcon className="text-gold mr-4" size={32} data-id="element-397" />
                    <h3 className="text-2xl font-bold text-ink" data-id="element-398">Education</h3>
                  </div>
                  <p className="text-xl font-semibold text-navy-900" data-id="element-399">
                    Associate of Applied Science
                  </p>
                  <p className="text-lg text-ink-muted" data-id="element-400">Process Technology</p>
                  <p className="text-base font-medium text-gold pt-2" data-id="element-401">
                    Victoria College, Texas
                  </p>
                </div>
              </Card3D>
            </motion.div>

            {/* Certifications */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-402">
              <div className="flex items-center mb-6" data-id="element-403">
                <AwardIcon className="text-gold mr-4" size={32} data-id="element-404" />
                <h3 className="text-2xl font-bold text-ink" data-id="element-405">
                  Key Certifications
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-406">
                <li className="flex items-start" data-id="element-407">
                  <ShieldCheckIcon className="text-navy-800 mr-3 mt-1 flex-shrink-0" size={20} data-id="element-408" />
                  <span className="text-lg text-ink-muted font-medium" data-id="element-409">
                    DOT Pipeline Certification
                  </span>
                </li>
                <li className="flex items-start" data-id="element-410">
                  <ShieldCheckIcon className="text-navy-800 mr-3 mt-1 flex-shrink-0" size={20} data-id="element-411" />
                  <span className="text-lg text-ink-muted font-medium" data-id="element-412">
                    Process Safety Management (PSM)
                  </span>
                </li>
                <li className="flex items-start" data-id="element-413">
                  <ShieldCheckIcon className="text-navy-800 mr-3 mt-1 flex-shrink-0" size={20} data-id="element-414" />
                  <span className="text-lg text-ink-muted font-medium" data-id="element-415">
                    Petroskills Onshore Gas Processing
                  </span>
                </li>
                <li className="flex items-start" data-id="element-416">
                  <ShieldCheckIcon className="text-navy-800 mr-3 mt-1 flex-shrink-0" size={20} data-id="element-417" />
                  <span className="text-lg text-ink-muted font-medium" data-id="element-418">
                    Modicon Unity PLC Programming
                  </span>
                </li>
                <li className="flex items-start" data-id="element-419">
                  <ShieldCheckIcon className="text-navy-800 mr-3 mt-1 flex-shrink-0" size={20} data-id="element-420" />
                  <span className="text-lg text-ink-muted font-medium" data-id="element-421">
                    Formosa Corporate Safety Training
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </PageTransition>;
}